import {
    EmailEntityId,
    FileId,
    MessageBodyAvailability,
    SentMessageId,
    UserMailboxMessageId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { stableQueryOptionsRpc } from "util/requests";

export interface MailboxAttachmentMeta {
    id: string;
    filename: string | null;
    contentType: string;
    encodedSize: number;
    oversized: boolean;
    savedFileId: FileId | null;
}

export interface MessageBodyData {
    html: string | null;
    plain: string | null;
    attachments: MailboxAttachmentMeta[];
    availability: MessageBodyAvailability;
}

const emptyBody: MessageBodyData = {
    html: null,
    plain: null,
    attachments: [],
    availability: "available",
};

export function useReceivedMessageBodyQuery(
    entityId: EmailEntityId | undefined,
    messageId: UserMailboxMessageId,
) {
    const trpcClient = trpc.useTRPC();
    return useQuery({
        ...trpcClient.mailbox.getMessageBody.queryOptions({
            entityId: entityId!,
            id: messageId,
        }),
        ...stableQueryOptionsRpc(),
        enabled: !!entityId,
        staleTime: 5 * 60 * 1000,
    });
}

export function useReceivedMessageBody(
    entityId: EmailEntityId | undefined,
    messageId: UserMailboxMessageId,
): MessageBodyData {
    const query = useReceivedMessageBodyQuery(entityId, messageId);
    if (!query.data) return emptyBody;
    return query.data as MessageBodyData;
}

export function useSentMessageBody(messageId: SentMessageId): MessageBodyData {
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.mailbox.getSentMessageBody.queryOptions({
            id: messageId,
        }),
        ...stableQueryOptionsRpc(),
        staleTime: 5 * 60 * 1000,
    });
    if (!query.data) return emptyBody;
    return query.data as MessageBodyData;
}
